import React, { useState } from 'react';
import { CheckCircle2Icon, ChevronDownIcon, MapPinIcon, QrCodeIcon } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { ResearchSource } from './ResearchSource';
const intelligence = [['INTERACTION', 'Who joined the experience?'], ['ENGAGEMENT', 'How did people interact with it?'], ['CATCH', 'What was caught?'], ['REDEMPTION', 'What was actually collected?'], ['EXPERIENCE', 'What did people think?'], ['INSIGHT', 'What can the brand learn?']];
const controls = [['AUDIENCE', 'Define who the experience is designed for.'], ['LOCATION', 'Run local, regional or national campaigns.'], ['QUANTITY', 'Control products available.'], ['EXPERIENCE', 'Choose the interaction.'], ['TIME', 'Schedule drops or always-on campaigns.'], ['REDEMPTION', 'Connect catches with participating locations.']];
export function BrandPlatform() {
  return <div id="for-brands" data-id="element-66">
      <ImagineCampaign data-id="element-67" />
      <BuiltForBrands data-id="element-68" />
      <CampaignManager data-id="element-69" />
      <CampaignControl data-id="element-70" />
      <Redemption data-id="element-71" />
      <Feedback data-id="element-72" />
    </div>;
}
function ImagineCampaign() {
  return <section className="bg-[#f7f5ef] px-5 py-24 md:px-10 md:py-32" aria-labelledby="imagine-heading" data-id="element-73">
      <div className="mx-auto max-w-[1280px]" data-id="element-74">
        <p className="text-xs font-extrabold tracking-[0.16em] text-black/45" data-id="element-75">
          IMAGINE THIS
        </p>
        <h2 id="imagine-heading" className="mt-5 max-w-5xl font-display text-5xl font-bold leading-[0.9] md:text-7xl" data-id="element-76">
          10,000 products.
          <br data-id="element-77" />
          One city.
          <br data-id="element-78" />
          <span className="text-[#ff4d17]" data-id="element-79">
            One experience people actually want to join.
          </span>
        </h2>
        <div className="mt-16 overflow-hidden border border-black/15 bg-white" data-id="element-80">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-black/10 px-5 py-4" data-id="element-81">
            <div data-id="element-82">
              <p className="text-xs font-bold tracking-[0.12em] text-black/45" data-id="element-83">
                DEMO CAMPAIGN
              </p>
              <p className="mt-1 font-display text-2xl font-bold" data-id="element-84">
                STOCKHOLM PRODUCT DROP
              </p>
            </div>
            <span className="rounded-full bg-[#ff4d17] px-3 py-1.5 text-xs font-extrabold text-white" data-id="element-85">
              ILLUSTRATIVE LIVE STATE
            </span>
          </div>
          <div className="grid lg:grid-cols-[1fr_0.85fr]" data-id="element-86">
            <div className="relative min-h-[400px] overflow-hidden bg-[#e9e5db] p-6" data-id="element-87">
              <div className="poster-rules absolute inset-0 opacity-50" data-id="element-88" />
              <MapPinIcon className="absolute left-[20%] top-[28%] text-[#ff4d17]" size={34} data-id="element-89" />
              <MapPinIcon className="absolute left-[60%] top-[48%] text-[#ff4d17]" size={34} data-id="element-90" />
              <MapPinIcon className="absolute bottom-[17%] right-[18%] text-[#ff4d17]" size={34} data-id="element-91" />
              <div className="absolute bottom-6 left-6 max-w-[330px] bg-black p-5 text-white" data-id="element-92">
                <p className="text-xs font-extrabold tracking-[0.14em] text-[#ff4d17]" data-id="element-93">
                  STOCKHOLM, SWEDEN
                </p>
                <p className="mt-3 font-display text-3xl font-bold leading-none" data-id="element-94">
                  10,000 KEXCHOKLAD ARE SWIMMING IN STOCKHOLM.
                </p>
                <p className="mt-4 text-sm font-bold" data-id="element-95">GO CATCH ONE.</p>
              </div>
            </div>
            <div className="p-6 md:p-8" data-id="element-96">
              <p className="text-[10px] font-extrabold tracking-[0.12em] text-black/40" data-id="element-97">
                ILLUSTRATIVE CAMPAIGN DATA
              </p>
              <div className="mt-8 grid grid-cols-2 gap-6" data-id="element-98">
                {[['10,000', 'PRODUCTS RELEASED'], ['8,421', 'PEOPLE JOINED'], ['31,204', 'CASTS'], ['7,842', 'PRODUCTS CAUGHT'], ['5,921', 'REDEEMED'], ['4.7 / 5', 'EXPERIENCE SCORE']].map(([value, label]) => <div key={label} data-id="element-99">
                    <p className="font-display text-3xl font-bold" data-id="element-100">{value}</p>
                    <p className="mt-1 text-[9px] font-extrabold tracking-[0.1em] text-black/55" data-id="element-101">
                      {label}
                    </p>
                  </div>)}
              </div>
              <p className="mt-8 border-t border-black/10 pt-4 text-xs font-bold leading-relaxed text-black/65" data-id="element-102">
                DEMO VALUES ONLY — a live campaign view would be underpinned by
                release, experience-start, catch, redemption, and feedback
                events.
              </p>
            </div>
          </div>
        </div>
        <ResearchSource sourceId="DP-02" status="illustrative" title="Stockholm Product Drop campaign card" summary="The campaign title, product reference, map markers, and values in this card are illustrative content used to demonstrate the shape of a campaign view." methodology="A production campaign record would require approved campaign configuration and defined event lineage. This card does not indicate a live campaign or reporting feature." className="mt-5" data-id="element-103" />
      </div>
    </section>;
}
function BuiltForBrands() {
  const reduceMotion = useReducedMotion();
  return <section className="bg-black px-5 py-24 text-white md:px-10 md:py-32" aria-labelledby="brands-heading" data-id="element-104">
      <div className="mx-auto max-w-[1280px]" data-id="element-105">
        <p className="text-xs font-extrabold tracking-[0.16em] text-white/45" data-id="element-106">
          BUILT FOR BRAND TEAMS
        </p>
        <h2 id="brands-heading" className="mt-5 max-w-4xl font-display text-5xl font-bold leading-[0.9] md:text-7xl" data-id="element-107">
          Sampling shouldn’t end when the product is handed out.
        </h2>
        <motion.p initial={reduceMotion ? false : {
        opacity: 1
      }} whileInView={{
        opacity: 0.28
      }} viewport={{
        once: false,
        amount: 0.7
      }} className="mt-20 font-display text-4xl font-bold line-through decoration-[#ff4d17] decoration-4 md:text-7xl" data-id="element-108">
          How many products did we distribute?
        </motion.p>
        <motion.p initial={reduceMotion ? false : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-5 font-display text-5xl font-bold text-[#ff4d17] md:text-8xl" data-id="element-109">
          What actually happened?
        </motion.p>
        <div className="mt-16 grid gap-y-10 border-t border-white/20 pt-10 md:grid-cols-2 md:gap-x-20" data-id="element-110">
          {intelligence.map(([title, copy], index) => <article key={title} className="flex gap-5 border-b border-white/15 pb-8" data-id="element-111">
              <span className="font-display text-3xl font-bold text-[#ff4d17]" data-id="element-112">
                0{index + 1}
              </span>
              <div data-id="element-113">
                <h3 className="text-sm font-extrabold tracking-[0.12em]" data-id="element-114">
                  {title}
                </h3>
                <p className="mt-3 text-white/60" data-id="element-115">{copy}</p>
              </div>
            </article>)}
        </div>
        <p className="mt-12 font-display text-4xl font-bold md:text-6xl" data-id="element-116">
          From distribution to understanding.
        </p>
      </div>
    </section>;
}
function CampaignManager() {
  const [filter, setFilter] = useState('ENGAGEMENT');
  const reduceMotion = useReducedMotion();
  const bars = filter === 'CATCHES' ? [32, 52, 44, 75, 81, 63, 93, 72] : filter === 'REDEMPTIONS' ? [18, 28, 35, 56, 53, 69, 62, 77] : [40, 58, 49, 68, 88, 72, 92, 85];
  return <section className="bg-[#f7f5ef] px-5 py-24 md:px-10 md:py-32" aria-labelledby="manager-heading" data-id="element-117">
      <div className="mx-auto max-w-[1280px]" data-id="element-118">
        <p className="text-xs font-extrabold tracking-[0.16em] text-black/45" data-id="element-119">
          SAMPLER CAMPAIGN MANAGER
        </p>
        <h2 id="manager-heading" className="mt-5 font-display text-5xl font-bold leading-[0.9] md:text-7xl" data-id="element-120">
          See the experience as it happens.
        </h2>
        <div className="mt-14 overflow-hidden rounded-[28px] border border-black/15 bg-[#11110f] text-white shadow-[14px_14px_0_#ff4d17]" data-id="element-121">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/15 px-5 py-5 md:px-7" data-id="element-122">
            <div data-id="element-123">
              <p className="font-bold" data-id="element-124">STOCKHOLM PRODUCT DROP</p>
              <p className="mt-1 text-xs text-white/45" data-id="element-125">
                ILLUSTRATIVE DASHBOARD DATA
              </p>
            </div>
            <span className="rounded-full border border-[#ff4d17] px-3 py-1.5 text-xs font-bold text-[#ff4d17]" data-id="element-126">
              DEMO STATUS: ILLUSTRATIVE LIVE STATE
            </span>
          </div>
          <div className="grid gap-5 p-5 md:p-7 lg:grid-cols-[1.35fr_0.65fr]" data-id="element-127">
            <div className="space-y-5" data-id="element-128">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3" data-id="element-129">
                {[['10,000', 'PRODUCTS AVAILABLE'], ['12,482', 'EXPERIENCES STARTED'], ['8,721', 'CATCHES'], ['6,283', 'REDEMPTIONS'], ['72%', 'REDEMPTION RATE'], ['4.6 / 5', 'EXPERIENCE SCORE']].map(([value, label]) => <div key={label} className="border border-white/15 p-4" data-id="element-130">
                    <p className="font-display text-3xl font-bold" data-id="element-131">{value}</p>
                    <p className="mt-2 text-[9px] font-extrabold tracking-[0.09em] text-white/45" data-id="element-132">
                      {label}
                    </p>
                  </div>)}
              </div>
              <div className="border border-white/15 p-5" data-id="element-133">
                <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-134">
                  <div data-id="element-135">
                    <p className="font-bold" data-id="element-136">CAMPAIGN MAP / DEMO</p>
                    <p className="text-xs text-white/45" data-id="element-137">
                      Stockholm activity / illustrative view
                    </p>
                  </div>
                  <div className="flex gap-1" role="group" aria-label="Illustrative campaign map filter" data-id="element-138">
                    {['ENGAGEMENT', 'CATCHES', 'REDEMPTIONS'].map(item => {
                    const isSelected = filter === item;
                    return <button key={item} type="button" aria-pressed={isSelected} onClick={() => setFilter(item)} className={`rounded-full px-3 py-1.5 text-[10px] font-bold transition-colors ${isSelected ? 'bg-[#ff4d17] text-white shadow-[0_0_0_1px_#ff4d17]' : 'border border-white/30 text-white/80 hover:border-white hover:text-white'}`} data-id="element-139">
                          {item}
                        </button>;
                  })}
                  </div>
                </div>
                <div className="relative mt-5 h-44 overflow-hidden border border-white/10" data-id="element-140">
                  <motion.div animate={{
                  x: filter === 'ENGAGEMENT' ? '0%' : filter === 'CATCHES' ? '40%' : '12%',
                  y: filter === 'REDEMPTIONS' ? '42%' : '0%',
                  scale: filter === 'ENGAGEMENT' ? 1 : filter === 'CATCHES' ? 0.78 : 1.15
                }} transition={{
                  duration: reduceMotion ? 0 : 0.35,
                  ease: [0.22, 1, 0.36, 1]
                }} className="absolute left-[25%] top-[30%] h-16 w-16 rounded-full bg-[#ff4d17]/30" data-id="element-141" />
                  <motion.div animate={{
                  x: filter === 'REDEMPTIONS' ? '-38%' : filter === 'CATCHES' ? '14%' : '0%',
                  y: filter === 'ENGAGEMENT' ? '0%' : '-18%',
                  scale: filter === 'REDEMPTIONS' ? 1.2 : 0.9
                }} transition={{
                  duration: reduceMotion ? 0 : 0.35,
                  ease: [0.22, 1, 0.36, 1]
                }} className="absolute bottom-[15%] right-[22%] h-20 w-20 rounded-full bg-[#ff4d17]/20" data-id="element-142" />
                  <p className="absolute bottom-3 left-3 text-[10px] font-bold text-white/70" data-id="element-143">
                    STOCKHOLM
                  </p>
                </div>
                <p className="mt-3 text-[10px] font-bold tracking-[0.08em] text-white/70" aria-live="polite" data-id="element-144">
                  SHOWING ILLUSTRATIVE {filter} VIEW — ANIMATED PROTOTYPE STATE
                </p>
              </div>
              <div className="border border-white/15 p-5" data-id="element-145">
                <p className="text-sm font-bold" data-id="element-146">CAMPAIGN FUNNEL</p>
                <div className="mt-5 flex h-28 items-end gap-2" data-id="element-147">
                  {bars.map((height, index) => <span key={index} className="flex h-full flex-1 items-end overflow-hidden" data-id="element-148">
                      <motion.span initial={reduceMotion ? false : {
                    scaleY: 0
                  }} animate={{
                    scaleY: height / 100
                  }} transition={{
                    delay: reduceMotion ? 0 : index * 0.04,
                    duration: 0.42,
                    ease: [0.22, 1, 0.36, 1]
                  }} className="h-full w-full origin-bottom bg-[#ff4d17]" data-id="element-149" />
                    </span>)}
                </div>
                <div className="mt-3 grid grid-cols-5 gap-1 text-[8px] font-bold tracking-[0.06em] text-white/45" data-id="element-150">
                  <span data-id="element-151">
                    12,482
                    <br data-id="element-152" />
                    EXPERIENCES
                  </span>
                  <span data-id="element-153">
                    10,204
                    <br data-id="element-154" />
                    ENGAGED
                  </span>
                  <span data-id="element-155">
                    8,721
                    <br data-id="element-156" />
                    CAUGHT
                  </span>
                  <span data-id="element-157">
                    6,283
                    <br data-id="element-158" />
                    REDEEMED
                  </span>
                  <span data-id="element-159">
                    4,921
                    <br data-id="element-160" />
                    FEEDBACK
                  </span>
                </div>
              </div>
            </div>
            <aside className="border border-white/15 p-5" data-id="element-161">
              <p className="text-xs font-extrabold tracking-[0.12em] text-[#ff4d17]" data-id="element-162">
                CAMPAIGN PULSE
              </p>
              <p className="mt-5 font-display text-4xl font-bold" data-id="element-163">Now.</p>
              <p className="mt-4 text-sm leading-relaxed text-white/60" data-id="element-164">
                Follow the physical product experience while the campaign is
                happening. This is a visual platform concept, not a claim of
                current reporting features.
              </p>
              <div className="mt-8 border-t border-white/15 pt-5 text-xs leading-loose text-white/50" data-id="element-165">
                ENGAGEMENT
                <br data-id="element-166" />
                CATCHES
                <br data-id="element-167" />
                REDEMPTIONS
                <br data-id="element-168" />
                FEEDBACK
              </div>
            </aside>
          </div>
        </div>
      </div>
    </section>;
}
function CampaignControl() {
  return <section className="bg-white px-5 py-24 md:px-10 md:py-32" aria-labelledby="control-heading" data-id="element-169">
      <div className="mx-auto max-w-[1280px]" data-id="element-170">
        <p className="text-xs font-extrabold tracking-[0.16em] text-black/45" data-id="element-171">
          YOUR PRODUCT. YOUR CAMPAIGN.
        </p>
        <h2 id="control-heading" className="mt-5 font-display text-5xl font-bold leading-[0.85] md:text-8xl" data-id="element-172">
          Decide who.
          <br data-id="element-173" />
          Where.
          <br data-id="element-174" />
          When. How.
        </h2>
        <div className="mt-14 grid border-l border-t border-black/15 md:grid-cols-2 lg:grid-cols-3" data-id="element-175">
          {controls.map(([title, copy]) => <article key={title} className="border-b border-r border-black/15 p-6 md:p-8" data-id="element-176">
              <p className="text-xs font-extrabold tracking-[0.12em] text-[#ff4d17]" data-id="element-177">
                {title}
              </p>
              <p className="mt-8 max-w-xs text-lg font-bold leading-tight" data-id="element-178">
                {copy}
              </p>
              <ChevronDownIcon className="mt-10 text-black/40" size={20} data-id="element-179" />
            </article>)}
        </div>
        <p className="mt-5 text-xs text-black/40" data-id="element-180">
          PLATFORM CONCEPT — controls illustrate the intended campaign setup
          direction.
        </p>
        <p className="mt-12 font-display text-4xl font-bold md:text-6xl" data-id="element-181">
          Sampling becomes a campaign tool.
          <br data-id="element-182" />
          <span className="text-black/35" data-id="element-183">Not a logistical event.</span>
        </p>
      </div>
    </section>;
}
function Redemption() {
  const [passShown, setPassShown] = useState(false);
  const reduceMotion = useReducedMotion();
  return <section className="bg-[#ff4d17] px-5 py-24 text-black md:px-10 md:py-32" aria-labelledby="redemption-heading" data-id="element-184">
      <div className="mx-auto grid max-w-[1280px] gap-10 lg:grid-cols-[1fr_0.75fr] lg:items-center" data-id="element-185">
        <div data-id="element-186">
          <p className="text-xs font-extrabold tracking-[0.16em] text-black/60" data-id="element-187">
            DIGITAL DISCOVERY. PHYSICAL PRODUCT.
          </p>
          <h2 id="redemption-heading" className="mt-5 font-display text-5xl font-bold leading-[0.88] md:text-7xl" data-id="element-188">
            Catch it on your phone.
            <br data-id="element-189" />
            Get it in the real world.
          </h2>
          <p className="mt-8 max-w-xl text-lg leading-relaxed text-black/80" data-id="element-190">
            A catch can live in the consumer account, then move through a simple
            partner redemption flow. This is product direction for the Sampler
            redemption experience.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3 text-sm font-extrabold" data-id="element-191">
            <span data-id="element-192">PHONE</span>
            <span data-id="element-193">↓</span>
            <span data-id="element-194">QR SCAN</span>
            <span data-id="element-195">↓</span>
            <span data-id="element-196">PRODUCT REDEEMED</span>
          </div>
        </div>
        <motion.div initial={reduceMotion ? false : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mx-auto w-full max-w-[340px] rounded-[42px] border-[8px] border-black bg-black p-2 shadow-[14px_14px_0_white]" data-id="element-197">
          <div className="rounded-[30px] bg-[#f7f5ef] p-6 text-center" data-id="element-198">
            <p className="text-xs font-extrabold tracking-[0.14em] text-[#ff4d17]" data-id="element-199">
              CONGRATS
            </p>
            <p className="mt-4 font-display text-3xl font-bold leading-none" data-id="element-200">
              YOU CAUGHT A KEXCHOKLAD.
            </p>
            <p className="mt-5 text-xs font-bold text-black/60" data-id="element-201">
              ILLUSTRATIVE CATCH
            </p>
            <div className="mx-auto mt-5 grid h-44 w-44 grid-cols-7 gap-1 bg-white p-3" data-id="element-202">
              {Array.from({
              length: 49
            }, (_, i) => <span key={i} className={i % 3 === 0 || i % 7 === 0 || i === 11 || i === 29 ? 'bg-black' : 'bg-white'} data-id="element-203" />)}
            </div>
            <button type="button" aria-pressed={passShown} onClick={() => setPassShown(current => !current)} className={`mt-6 w-full rounded-xl py-4 text-sm font-extrabold transition-colors ${passShown ? 'bg-[#ff4d17] text-white' : 'bg-black text-white hover:bg-black/80'}`} data-id="element-204">
              {passShown ? 'PASS SHOWN — TAP TO RESET' : 'SHOW TO STAFF'}
            </button>
            <div className="mt-4 flex items-center justify-center gap-2 text-sm font-bold text-emerald-800" aria-live="polite" data-id="element-205">
              <CheckCircle2Icon size={18} aria-hidden="true" data-id="element-206" />
              {passShown ? 'DEMO PASS DISPLAYED' : 'REDEMPTION FLOW CONCEPT'}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
function Feedback() {
  const [intent, setIntent] = useState<string | null>(null);
  const [word, setWord] = useState<string | null>(null);
  return <section className="bg-[#f7f5ef] px-5 py-24 md:px-10 md:py-32" aria-labelledby="feedback-heading" data-id="element-207">
      <div className="mx-auto grid max-w-[1280px] gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center" data-id="element-208">
        <div data-id="element-209">
          <p className="text-xs font-extrabold tracking-[0.16em] text-black/45" data-id="element-210">
            THE EXPERIENCE CONTINUES
          </p>
          <h2 id="feedback-heading" className="mt-5 font-display text-5xl font-bold leading-[0.9] md:text-7xl" data-id="element-211">
            The product was redeemed.
            <br data-id="element-212" />
            Now learn what happened.
          </h2>
          <p className="mt-7 max-w-lg text-lg leading-relaxed text-black/65" data-id="element-213">
            Every product experience can create a signal. This is the intended
            feedback direction, not an AI or intelligence claim.
          </p>
        </div>
        <div className="relative border border-black/15 bg-white p-6 md:p-8" data-id="element-214">
          <p className="text-xs font-extrabold tracking-[0.14em] text-[#ff4d17]" data-id="element-215">
            PRODUCT DIRECTION
          </p>
          <h3 className="mt-7 font-display text-4xl font-bold" data-id="element-216">HOW WAS IT?</h3>
          <p className="mt-4 text-3xl tracking-[0.18em] text-[#ff4d17]" data-id="element-217">
            ★★★★★
          </p>
          <p className="mt-8 text-sm font-bold" data-id="element-218">WOULD YOU BUY IT?</p>
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-219">
            {['YES', 'MAYBE', 'NO'].map(item => <button type="button" key={item} aria-pressed={intent === item} onClick={() => setIntent(item)} className={`border px-4 py-2 text-xs font-extrabold transition-colors ${intent === item ? 'border-black bg-black text-white' : 'border-black/20 hover:bg-black hover:text-white'}`} data-id="element-220">
                {item}
              </button>)}
          </div>
          <p className="mt-8 text-sm font-bold" data-id="element-221">DESCRIBE IT IN ONE WORD.</p>
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-222">
            {['FRESH', 'SMOOTH', 'SWEET', 'SURPRISING', 'OTHER'].map(item => <button type="button" key={item} aria-pressed={word === item} onClick={() => setWord(item)} className={`border px-3 py-2 text-xs font-bold transition-colors ${word === item ? 'border-[#ff4d17] bg-[#ff4d17] text-white' : 'border-black/15 text-black/60 hover:border-black'}`} data-id="element-223">
                {item}
              </button>)}
          </div>
          <div className="mt-10 flex items-center gap-3 border-t border-black/10 pt-5 text-sm font-bold" data-id="element-224">
            <QrCodeIcon size={19} className="text-[#ff4d17]" aria-hidden="true" data-id="element-225" />
            <span data-id="element-226">
              {intent || word ? 'ILLUSTRATIVE FEEDBACK SELECTION RECORDED' : 'SIGNAL RETURNS TO CAMPAIGN DATA'}
            </span>
          </div>
          <p className="mt-3 text-xs font-bold text-black/60" aria-live="polite" data-id="element-227">
            {intent && word ? `DEMO RESPONSE: ${intent} · ${word}` : 'SELECT AN OPTION TO PREVIEW THE FEEDBACK STATE.'}
          </p>
        </div>
      </div>
    </section>;
}